import {
    Button,
    ButtonFileUpload,
    checkPhoneLengthFromMask,
    Dictionary,
    DropDown,
    getMaskFromName,
    getMaskFromPhone,
    ModalConfirm,
    requiredRule,
    useActionsBtnContext,
    useErrorState,
    useToastContext,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import uploadFile from 'axios/fileUploadApi';
import { getContractFiles, getPayment, updateOrderStatus } from 'axios/merchantApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { sumBy } from 'lodash';
import { MerchantTradeOrgContact } from 'models/api/merchant';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useMerchantOrderContext } from 'providers/MerchantOrderProvider';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, UseQueryResult } from 'react-query';
import { Card, CardBody, Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { FileUploadNotificator, UploadResult } from 'utils/fileUploadNotificator';
import { getCultureVariant } from 'utils/i18n';
import { LIMIT_STATE_APPROVED, LIMIT_STATE_DECLINED_SCORE } from '../../../../constans/limitStates';
import { useMerchantWorkplaceState } from '../Merchant';
import MerchantContacts from '../MerchantContacts';
import MerchantOrderProducts from './MechantOrderProducts';
import MerchantOrderClientSelfie from './MerchantOrderClientSelfie';
import MerchantOrderContractFile from './MerchantOrderContractFile';
import MerchantOrderContractUploadButton from './MerchantOrderContractUploadButton';
import MerchantOrderCreditLimit from './MerchantOrderCreditLimit';
import { MerchantOrderModalFormType } from './MerchantOrderModal';
import MerchantOrderStatistic from './MerchantOrderStatistic';
import { numberFromLocaleString } from 'utils/number';

type MerchantOrderFormProps = {
    toggle: Dispatch<SetStateAction<boolean>>;
    contacts: MerchantTradeOrgContact[];
    getContactsQuery: UseQueryResult<MerchantTradeOrgContact[], unknown>;
    tariffGroups: DropDown[];
    tariffGroupsFetching: boolean;
    settings: Dictionary<string> | undefined;
    settingsFetching: boolean;
};

type MaxSumSettings = {
    minSum: number;
    maxSum: number;
};

const defaultSumSettings: MaxSumSettings = {
    minSum: 1,
    maxSum: 1000000000,
};

const MerchantOrderForm = ({
    toggle,
    contacts,
    getContactsQuery,
    settings,
    settingsFetching,
    tariffGroups,
    tariffGroupsFetching,
}: MerchantOrderFormProps) => {
    const api = useApi();
    const { t, i18n } = useTranslation();
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();
    const { cultures } = useGeneralSettingsContext();
    const toasts = useToastContext();
    const { currentItem, fetchQuery, fetchCurrentItem } = useMerchantWorkplaceState();
    const { addRequest } = useActionsBtnContext();
    const { watch } = useFormContext<MerchantOrderModalFormType>();

    const {
        clientCreditLimit,
        clientCreditLimitRequired,
        tariffGroupId,
        requestClientLimitLoading,
        waitClientLimitResponse,
    } = useMerchantOrderContext();

    const [contractFileId, setContractFileId] = useState<number | null>(null);
    const [signedContractFileId, setSignedContractFile] = useState<number | null>(null);
    const [notifyModal, setNotifyModal] = useState(false);
    const [maxSumError, setMaxSumError] = useState(false);

    const products = watch('products'),
        totalCount = sumBy(products, (p) => p?.productCnt ?? 0),
        totalSum = sumBy(
            products,
            (p) =>
                numberFromLocaleString(p?.productCost ?? '0', i18n.language) * (p?.productCnt ?? 0)
        );

    const isEditable = currentItem == null;

    const goodshipEnabled =
        !isEditable &&
        currentItem.statusCode === 'signed' &&
        hasRole(['merchantGetContractFiles', 'merchantUpdateOrderStatus']);

    const contractFilesEnabled =
        hasRole('merchantGetContractFiles') &&
        !isEditable &&
        currentItem.statusCode === 'clientconfirm';

    const findFirstContractDoc = (docs: Dictionary<number>): number | null =>
        cultures
            .map((lang) => docs[`contract_${getCultureVariant(lang.code)}_pdf`])
            .find(Boolean) ?? null;

    const sumSettings: MaxSumSettings = useMemo(
        () =>
            settings
                ? {
                      minSum:
                          (settings['CashLoanMinSum'] && parseInt(settings['CashLoanMinSum'])) ||
                          defaultSumSettings.minSum,
                      maxSum:
                          (settings['CashLoanMaxSum'] && parseInt(settings['CashLoanMaxSum'])) ||
                          defaultSumSettings.maxSum,
                  }
                : defaultSumSettings,
        [settings]
    );

    const { isFetching: contractFilesFetching, refetch: refetchContractFiles } = useQuery(
        'merchantContractFiles',
        () => getContractFiles(api, currentItem?.orderId),
        {
            onSuccess: (data) => {
                if (data) {
                    setContractFileId(
                        data[`contract_${getCultureVariant(i18n.language)}_pdf`] ??
                            findFirstContractDoc(data)
                    );
                    setSignedContractFile(data['contract_signed']);
                }
            },
            onError: (err) => handleRequestError(err),
            enabled:
                !isEditable &&
                hasRole('merchantGetContractFiles') &&
                ['clientconfirm', 'goodship'].includes(currentItem.statusCode),
        }
    );

    const getPaymentQuery = useQuery(
        ['PreOrderPayment', tariffGroupId, totalSum],
        () => getPayment(api, tariffGroupId, totalSum, currentItem?.id),
        {
            enabled:
                !!tariffGroupId && totalSum >= sumSettings.minSum && totalSum <= sumSettings.maxSum,
            onError: (err) => handleRequestError(err),
        }
    );

    useEffect(() => {
        if (getPaymentQuery.data && getPaymentQuery.data.adjustedSum > sumSettings.maxSum) {
            setMaxSumError(true);
            toasts.addToast({
                title: t('BackOfficeToastHeaderWarning'),
                type: 'info',
                description: `${t(
                    'BackOfficeMerchantSumLimitError'
                )} ${sumSettings.maxSum.toLocaleString()}`,
            });
            return;
        }
        setMaxSumError(false);
    }, [totalSum, tariffGroupId]);

    const uploadCallback = useCallback(
        (res: UploadResult) => res === 'success' && refetchContractFiles(),
        [refetchContractFiles]
    );

    const fileExtensionValidator = useCallback(
        (files: FileList) => {
            const allowed = ['pdf', 'jpg', 'jpeg', 'gif', 'png', 'bmp'];
            const invalid = Array.from(files).some(
                (f) => !allowed.some((e) => f.name.toLowerCase().endsWith(`.${e}`))
            );

            if (invalid) {
                toasts.addToast({
                    title: t('BackOfficeToastHeaderWarning'),
                    type: 'warning',
                    autoDeleteTime: null,
                    description: t('BackOfficeFileImportExtensionError').replace(
                        '{0}',
                        allowed.join(', ')
                    ),
                });
                return false;
            }
            return true;
        },
        [toasts, t]
    );

    const submitDisabled = useMemo(() => {
        return (
            !hasRole('merchantOrderCreate') ||
            maxSumError ||
            !tariffGroupId ||
            clientCreditLimitRequired ||
            !getPaymentQuery.data ||
            ([LIMIT_STATE_APPROVED, LIMIT_STATE_DECLINED_SCORE].indexOf(
                clientCreditLimit.limitState
            ) >= 0 &&
                getPaymentQuery.data?.adjustedSum > (clientCreditLimit.amount ?? 0))
        );
    }, [
        hasRole,
        maxSumError,
        tariffGroupId,
        clientCreditLimitRequired,
        clientCreditLimit.limitState,
        totalSum,
        getPaymentQuery.data,
    ]);

    const goodShippedMutation = useMutation({
        mutationFn: () => updateOrderStatus(api, currentItem?.orderId ?? null),
        onSuccess: () => {
            fetchCurrentItem();
            fetchQuery.refetch();
        },
    });

    const handleToggle = () => {
        if (goodshipEnabled) {
            addRequest({
                content: t('BackOfficeMerchantAttention'),
                confirmCallback: (res) => {
                    if (res) {
                        toggle(true);
                    }
                },
            });
        } else {
            toggle(false);
        }
    };

    return (
        <>
            <ModalHeader toggle={handleToggle}>
                {!isEditable
                    ? `${t('BackOfficeOrder')} #${currentItem.orderId ?? currentItem.id}`
                    : t('BackOfficeNew')}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col xs="8">
                        <Card className="mb-3" hidden={isEditable}>
                            <CardBody>
                                <Row>
                                    <MerchantOrderClientSelfie
                                        selfieFileId={currentItem?.selfieFileId ?? null}
                                    />
                                    <Col xs="5">
                                        <dl>
                                            <dt>
                                                <Trans i18nKey="BackOfficeLastName" />
                                            </dt>
                                            <dd>
                                                {currentItem?.lastName.length
                                                    ? currentItem?.lastName
                                                    : '--'}
                                            </dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeFirstName" />
                                            </dt>
                                            <dd>
                                                {currentItem?.firstName.length
                                                    ? currentItem?.firstName
                                                    : '--'}
                                            </dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeMiddleName" />
                                            </dt>
                                            <dd>
                                                {currentItem?.middleName.length
                                                    ? currentItem?.middleName
                                                    : '--'}
                                            </dd>
                                        </dl>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                        {contractFilesEnabled && (
                            <Row className="mb-5">
                                <Col xs="6">
                                    <div className="h-50">
                                        <Trans i18nKey="BackOfficeMerchantFirstStep" />
                                    </div>
                                    <MerchantOrderContractFile
                                        color="primary"
                                        contractFilesFetching={contractFilesFetching}
                                        fileId={contractFileId}
                                        btnName={t('BackOfficeMerchantContractDownload')}
                                        fileType="contract"
                                    />
                                </Col>
                                <Col xs="6">
                                    <div className="h-50">
                                        <Trans i18nKey="BackOfficeMerchantSecondStep" />
                                    </div>
                                    {contractFilesFetching || signedContractFileId ? (
                                        <MerchantOrderContractFile
                                            color="default"
                                            contractFilesFetching={contractFilesFetching}
                                            fileId={signedContractFileId}
                                            btnName={t('BackOfficeMerchantContractSigned')}
                                            className="btn-outline-secondary"
                                            canBeDeleted={hasRole('merchantDeleteContractFile')}
                                            setFileId={setSignedContractFile}
                                            fileType="contractSigned"
                                        />
                                    ) : (
                                        hasRole('merchantUploadSignedContract') && (
                                            <ButtonFileUpload
                                                onFilesSelect={(files) => {
                                                    const uploader = new FileUploadNotificator(
                                                        (file, onUploadProgress) =>
                                                            uploadFile(
                                                                api,
                                                                '/merchant',
                                                                file,
                                                                onUploadProgress,
                                                                {
                                                                    orderId: currentItem?.orderId,
                                                                }
                                                            ),
                                                        toasts,
                                                        uploadCallback
                                                    );
                                                    return uploader.uploadFiles(files);
                                                }}
                                                validate={fileExtensionValidator}
                                                button={(onClick, isUploading) => (
                                                    <MerchantOrderContractUploadButton
                                                        onClick={onClick}
                                                        isUploading={isUploading}
                                                    />
                                                )}
                                            />
                                        )
                                    )}
                                </Col>
                            </Row>
                        )}
                        <ValidatedInput<MerchantOrderModalFormType>
                            id="isUseDefault"
                            className="my-3"
                            type="checkbox"
                            name="isMobileApp"
                            label={t('BackOfficeIsMobileApp')}
                            disabled={!isEditable}
                        />
                        <ValidatedInput<MerchantOrderModalFormType>
                            id="mod-phone-number"
                            name="phoneNumber"
                            maskProps={{
                                mask: getMaskFromName('Default'),
                                changeMask: getMaskFromPhone,
                            }}
                            rules={{
                                required: requiredRule,
                                validate: (value) => checkPhoneLengthFromMask(value),
                            }}
                            disabled={
                                !isEditable || requestClientLimitLoading || waitClientLimitResponse
                            }
                            label={t('BackOfficePhoneNumber')}
                        />
                        {isEditable && hasRole('merchantGetClientLimit') && (
                            <MerchantOrderCreditLimit />
                        )}
                        {!clientCreditLimitRequired && (
                            <MerchantOrderProducts
                                settingsFetching={settingsFetching}
                                settings={settings ?? null}
                                isEditable={isEditable}
                            />
                        )}
                        {isEditable && !clientCreditLimitRequired && (
                            <ValidatedDropDownList<MerchantOrderModalFormType>
                                name="tariffId"
                                rules={{ required: requiredRule }}
                                disabled={!tariffGroups.length}
                                items={tariffGroups}
                                isLoading={tariffGroupsFetching}
                                label={t('BackOfficeTariff')}
                            />
                        )}
                        {!isEditable && (
                            <ValidatedInput
                                className="mt-3"
                                name="tariffName"
                                label={t('BackOfficeTariff')}
                                disabled
                            />
                        )}
                    </Col>
                    <Col xs="4">
                        <MerchantOrderStatistic
                            signedContractFileId={
                                currentItem?.statusCode === 'goodship' ? signedContractFileId : null
                            }
                            tariffGroups={tariffGroups}
                            contractFilesFetching={contractFilesFetching}
                            totalSum={totalSum}
                            totalCount={totalCount}
                            getPaymentQuery={getPaymentQuery}
                        />
                        <MerchantContacts contacts={contacts} getContactsQuery={getContactsQuery} />
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter className="modal-footer justify-content-between">
                <Button color="secondary" onClick={() => toggle(false)}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
                <Button
                    type="submit"
                    color="success"
                    hidden={!isEditable}
                    disabled={submitDisabled}
                >
                    <Trans i18nKey="BackOfficeSave" />
                </Button>
                {goodshipEnabled && (
                    <>
                        <ModalConfirm
                            content={t('BackOfficeMerchantGoodshippedConfirmMsg')}
                            confirmButtonContent={t('BackOfficeOk')}
                            cancelButtonContent={t('BackOfficeCancel')}
                            isOpen={notifyModal}
                            toggle={(v) => {
                                setNotifyModal(!notifyModal);
                                if (v) goodShippedMutation.mutate();
                            }}
                        />
                        <Button
                            color="danger"
                            disabled={goodShippedMutation.isLoading}
                            onClick={() => setNotifyModal(true)}
                        >
                            <Trans i18nKey="BackOfficeMerchantGoodShipped" />
                        </Button>
                    </>
                )}
            </ModalFooter>
        </>
    );
};

export default MerchantOrderForm;
